import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { ErrorState, Skeleton } from '@/components/feedback';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import { usePublicProperty } from '@/hooks/usePublicProperties';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import { trackPublicPropertyAction } from '@/lib/analytics/events';
import { Seo } from '@/components/seo';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { formatAssetTypeLabel } from '@/lib/constants/propertyOptions';
import {
  formatAvailabilityDateLabel,
  formatUnitLabel,
  humanizeAddressText,
} from '@/lib/utils/propertyFormatting';
import { applyTemplate } from '@/lib/utils/textTemplates';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  LEASEABLE_UNIT_STATUSES,
  UnitShowingTriggerButton,
} from '@/routes/publicProperties/components/UnitEngagementDialog';
import { PublicDetailHero } from '@/routes/publicProperties/components/PublicDetailHero';
import type {
  PublicPropertyDetail as PublicPropertyDetailResult,
  PublicPropertySummary,
  PublishedUnitSummary,
} from '@/lib/types/publicProperty';
import type { PropertyAddress, UnitAvailabilityStatus } from '@/lib/types/property';
import type {
  AvailabilitiesMapUnitRowContent,
  PropertyDetailContent,
  PropertyDetailKeyFiguresContent,
} from '@/lib/types/siteSettings';
import {
  formatAnnualNetPsfRentRange,
  formatAnnualPsfRentBreakdown,
  formatMonthlyLeaseAmount,
} from './rentFormatting';

type UnitFilterValue = 'all' | `${number}`;

interface UnitFiltersState {
  size: UnitFilterValue;
  floor: UnitFilterValue;
  price: UnitFilterValue;
}

interface UnitFilterOptions {
  sizes: number[];
  floors: string[];
  prices: string[];
}

const DEFAULT_UNIT_FILTERS: UnitFiltersState = {
  size: 'all',
  floor: 'all',
  price: 'all',
};

const DEFAULT_PROPERTY_DETAIL_CONTENT: PropertyDetailContent = {
  messages: {
    unpublishedMessage: 'This listing may have been unpublished.',
    ctaLabel: 'Explore availabilities',
  },
  navigation: {
    overviewLabel: 'Overview',
    highlightsLabel: 'Highlights',
    locationLabel: 'Location',
    availabilityLabel: 'Availability',
  },
  availabilityStrip: {
    noUnitsLabel: 'No units currently available',
    availableUnitsTemplate: '{available} of {total} units available',
    updatedLabel: 'Availability updated regularly.',
  },
  overview: {
    eyebrow: 'Property',
    viewOnMapLabel: 'View on map',
    titleTemplate: '{assetType} in {submarket}',
  },
  units: {
    eyebrow: 'Available units',
    title: 'Availability',
    listedTemplate: '{filtered} of {total} listed',
    emptyAllMessage: 'No units are currently listed for lease.',
    emptyFilteredMessage:
      'No units match the current filters. Clear filters to see all available suites.',
    unitCountSingular: 'unit',
    unitCountPlural: 'units',
    floorPlanFallback: 'Floor plan coming soon',
    monthlyLeaseLabel: 'Est. monthly lease',
    contactLabel: 'Contact',
    contactFallback: 'Book a showing to connect',
    bookButtonLabel: 'Book a Showing',
    viewDetailsLabel: 'View full unit details',
  },
  unitsTable: {
    suiteHeader: 'Suite',
    floorHeader: 'Floor',
    sizeHeader: 'Size (SF)',
    monthlyLeaseHeader: 'Est. monthly lease',
    rateHeader: 'Rate',
    actionHeader: 'Action',
    noUnitsMessage: 'No units are currently listed for lease.',
    noMatchesMessage:
      'No units match the current filters. Clear filters to see all available suites.',
  },
  unitFilters: {
    sizeLabel: 'Size',
    floorLabel: 'Floor',
    rateLabel: 'Rate',
    sizeAllLabel: 'All sizes ({count})',
    floorAllLabel: 'All floors',
    rateAllLabel: 'All rates',
  },
  highlights: {
    eyebrow: 'Highlights',
    title: 'Highlights',
    description:
      'A quick snapshot based on the current listing. Confirm details during your showing.',
    divisibleLabel: 'Potentially divisible suites',
    incentivesLabel: 'Incentives may be available',
    floorPlanLabel: 'Floor plan preview available',
    emptyMessage:
      'Book a showing to review building details, fit-up options, and current availability.',
  },
  location: {
    title: 'Location',
    directionsLabel: 'Directions',
  },
  keyFigures: {
    title: 'Key figures',
    subtitle: 'Snapshot',
    totalRentableLabel: 'Total Rentable SF',
    unitsLiveLabel: 'Units Live',
    incentivesLabel: 'Incentives',
    divisibleLabel: 'Divisible',
  },
  labels: {
    rentFallback: 'Contact for rates',
    availabilityRangeFallback: 'Flexible availability',
    availabilitySingleFallback: 'Available',
    unitAvailabilityImmediateLabel: 'Immediate',
    unitAvailabilityNegotiableLabel: 'Negotiable',
  },
};

const DEFAULT_MAP_UNIT_ROW_CONTENT: AvailabilitiesMapUnitRowContent = {
  unitLabelTemplate: 'Unit {unitLabel}',
  levelLabelTemplate: '{floor}',
  divisibleLabel: 'Divisible',
  incentivesLabel: 'Incentives',
};

export function PublicPropertyDetail(): JSX.Element {
  const { propertySlug } = useParams<{ propertySlug: string }>();
  const propertySlugSafe = typeof propertySlug === 'string' ? propertySlug : '';
  const identifier = useMemo(() => propertySlugSafe.trim().toUpperCase(), [propertySlugSafe]);
  const request = identifier.length > 0 ? { identifier } : null;
  const { data, isLoading, isFetching, error } = usePublicProperty(request);
  const showLoading = isLoading || isFetching;
  const siteSettingsQuery = useSiteSettingsDetail();
  const propertyDetailContent = mergeContentWithDefaults(
    siteSettingsQuery.data?.publicContent.propertyDetail ?? null,
    DEFAULT_PROPERTY_DETAIL_CONTENT,
  );
  const summary = data?.summary ?? null;
  const canonicalPath =
    propertySlugSafe.length > 0
      ? `/properties/${propertySlugSafe.toLowerCase()}`
      : '/availabilities';
  const seoTitle = summary ? formatAddress(summary) : 'Property detail';
  const seoDescription = summary
    ? summary.marketingSummary.trim().length > 0
      ? summary.marketingSummary
      : applyTemplate(propertyDetailContent.overview.titleTemplate, {
          assetType: formatAssetTypeLabel(summary.assetType),
          submarket: summary.submarket,
        })
    : propertyDetailContent.messages.unpublishedMessage;
  const canonicalUrl = resolveCanonicalUrl(canonicalPath);
  const structuredData = useMemo(
    () => (summary ? buildPropertyStructuredData(summary, canonicalUrl) : null),
    [summary, canonicalUrl],
  );

  return (
    <PublicPageLayout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        structuredData={structuredData}
      />
      <PublicPageSection
        tone="base"
        spacing="none"
        aria-label="Property details"
        stacked={false}
        container
        containerClassName="flex flex-col gap-6 pb-16 md:gap-8 md:pb-20"
        className="overflow-hidden"
      >
        <AsyncState
          data={data ?? null}
          isLoading={showLoading}
          error={error}
          skeleton={<PropertyDetailSkeleton />}
          errorFallback={(caught) => {
            const message =
              caught instanceof Error
                ? caught.message
                : propertyDetailContent.messages.unpublishedMessage;
            return (
              <div className="flex flex-col gap-4">
                <ErrorState description={message} />
                <p className="text-sm text-text-primary/70">
                  {propertyDetailContent.messages.unpublishedMessage}{' '}
                  <Link to="/availabilities" className="font-semibold text-brand-primary underline">
                    {propertyDetailContent.messages.ctaLabel}
                  </Link>
                </p>
              </div>
            );
          }}
        >
          {(result) => <PropertyDetailContent {...result} />}
        </AsyncState>
      </PublicPageSection>
    </PublicPageLayout>
  );
}

function PropertyDetailContent({ summary }: PublicPropertyDetailResult): JSX.Element {
  const [activeUnitId, setActiveUnitId] = useState<string | null>(() =>
    findDefaultUnitId(summary.publishedUnits),
  );
  const [unitFilters, setUnitFilters] = useState<UnitFiltersState>(DEFAULT_UNIT_FILTERS);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const siteSettingsQuery = useSiteSettingsDetail();
  const siteSettings = siteSettingsQuery.data;
  const publicContent = siteSettingsQuery.data?.publicContent ?? null;
  const propertyDetailContent = mergeContentWithDefaults(
    publicContent?.propertyDetail ?? null,
    DEFAULT_PROPERTY_DETAIL_CONTENT,
  );
  const mapUnitRowContent = mergeContentWithDefaults(
    publicContent?.availabilitiesPage.mapUnitRow ?? null,
    DEFAULT_MAP_UNIT_ROW_CONTENT,
  );
  const filterOptions = useMemo(
    () => buildUnitFilterOptions(summary.publishedUnits),
    [summary.publishedUnits],
  );
  const filteredUnits = useMemo(
    () => filterUnits(summary.publishedUnits, unitFilters),
    [summary.publishedUnits, unitFilters],
  );
  const stats = useMemo(
    () => buildStats(summary, propertyDetailContent.keyFigures),
    [summary, propertyDetailContent.keyFigures],
  );
  const cover = resolveCover(summary);
  const unitPreviewImage = resolveUnitPreviewImage(summary, cover);
  const rentLabel = formatRentRange(summary.publishedUnits, propertyDetailContent.labels);
  const availabilityLabel = formatAvailability(
    summary.earliestAvailability,
    summary.latestAvailability,
    propertyDetailContent.labels,
  );
  const marketingSummary = summary.marketingSummary;
  const heroMetaLine = `${formatNumber(summary.totalRentableSf)} SF · ${formatAssetTypeLabel(
    summary.assetType,
  )} · ${summary.submarket}`;

  useEffect(() => {
    const defaultUnit = findDefaultUnitId(filteredUnits);
    if (filteredUnits.length === 0) {
      setActiveUnitId(null);
      return;
    }
    const exists = filteredUnits.some((unit) => unit.id === activeUnitId);
    if (!exists) {
      setActiveUnitId(defaultUnit);
    }
  }, [filteredUnits, activeUnitId]);

  const selectedUnit = useMemo(() => {
    if (filteredUnits.length === 0) {
      return null;
    }
    const match = filteredUnits.find((unit) => unit.id === activeUnitId);
    return match ?? filteredUnits[0];
  }, [filteredUnits, activeUnitId]);
  const heroUnit = selectedUnit;
  const heroContactEmail =
    heroUnit && typeof heroUnit.contactEmail === 'string' && heroUnit.contactEmail.length > 0
      ? heroUnit.contactEmail
      : null;
  const bookingEmail: string | null =
    typeof siteSettings?.bookingEmail === 'string' && siteSettings.bookingEmail.length > 0
      ? siteSettings.bookingEmail
      : heroContactEmail;

  const handleUnitSelect = (unit: PublishedUnitSummary) => {
    if (activeUnitId === unit.id) {
      return;
    }
    setActiveUnitId(unit.id);
    trackPublicPropertyAction({
      propertyId: summary.id,
      propertyCode: summary.propertyCode,
      action: 'unit_view',
      unitId: unit.id,
      readiness: unit.availabilityStatus,
    });
  };

  const address = formatAddress(summary);
  const leaseableCount = summary.publishedUnits.filter((unit) =>
    LEASEABLE_UNIT_STATUSES.has(unit.availabilityStatus),
  ).length;

  const unitsSectionRef = useRef<HTMLDivElement | null>(null);
  const [activeSection, setActiveSection] = useState<
    'overview' | 'amenities' | 'location' | 'availability'
  >('overview');

  useEffect(() => {
    const sectionIds: Array<{ id: string; key: typeof activeSection }> = [
      { id: 'overview-section', key: 'overview' },
      { id: 'amenities-section', key: 'amenities' },
      { id: 'location-section', key: 'location' },
      { id: 'units-section', key: 'availability' },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleEntries.length === 0) {
          return;
        }

        const topEntry = visibleEntries[0];
        const matchedSection = sectionIds.find((section) => section.id === topEntry.target.id);
        if (matchedSection) {
          setActiveSection(matchedSection.key);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -60% 0px',
        threshold: 0.25,
      },
    );

    sectionIds.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleSectionNavClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }
    element.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const heroGallery = useMemo(() => buildHeroGallery(summary), [summary]);
  const heroGalleryCount = heroGallery.length;
  const hasMultipleHeroImages = heroGalleryCount > 1;
  const listedLabel = applyTemplate(propertyDetailContent.units.listedTemplate, {
    filtered: filteredUnits.length,
    total: summary.publishedUnits.length,
  });
  const overviewTitle = applyTemplate(propertyDetailContent.overview.titleTemplate, {
    assetType: formatAssetTypeLabel(summary.assetType),
    submarket: summary.submarket,
  });

  return (
    <>
      <div className="flex w-full min-w-0 max-w-full flex-col gap-6 md:gap-8">
        <PublicDetailHero
          cover={cover}
          badges={[formatAssetTypeLabel(summary.assetType), summary.submarket]}
          title={address}
          meta={[heroMetaLine, `${rentLabel} · ${availabilityLabel}`]}
          galleryCount={hasMultipleHeroImages ? heroGalleryCount : undefined}
          gallery={heroGallery}
        />

        <DesktopSectionNav
          activeSection={activeSection}
          onNavigate={handleSectionNavClick}
          labels={propertyDetailContent.navigation}
        />
        <MobileSectionNav
          activeSection={activeSection}
          onNavigate={handleSectionNavClick}
          labels={propertyDetailContent.navigation}
        />

        <AvailabilityStrip
          totalUnits={summary.publishedUnits.length}
          leaseableCount={leaseableCount}
          readinessStates={summary.readinessStates}
          content={propertyDetailContent.availabilityStrip}
        />

        <KeyFiguresCard stats={stats} content={propertyDetailContent.keyFigures} />

        <section
          ref={unitsSectionRef}
          id="units-section"
          className="w-full scroll-mt-28 border border-surface-subtle bg-white p-5 md:p-6"
          tabIndex={-1}
        >
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
              {propertyDetailContent.units.eyebrow}
            </p>
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-semibold text-brand-primary">
                {propertyDetailContent.units.title}
              </h3>
              <span className="text-sm text-text-primary/70">{listedLabel}</span>
            </div>
          </div>
          <UnitsTable
            summary={summary}
            units={filteredUnits}
            allUnits={summary.publishedUnits}
            activeUnitId={activeUnitId}
            filters={unitFilters}
            filterOptions={filterOptions}
            bookingEmail={bookingEmail}
            unitsContent={propertyDetailContent.units}
            unitsTableContent={propertyDetailContent.unitsTable}
            unitFiltersContent={propertyDetailContent.unitFilters}
            mapUnitRowContent={mapUnitRowContent}
            availabilityLabels={propertyDetailContent.labels}
            onFiltersChange={(next) => {
              setUnitFilters(next);
              const nextUnits = filterUnits(summary.publishedUnits, next);
              setActiveUnitId(findDefaultUnitId(nextUnits));
            }}
            unitPreviewImageUrl={unitPreviewImage}
            onSelect={handleUnitSelect}
          />
        </section>

        <article
          id="overview-section"
          className="flex w-full scroll-mt-28 flex-col gap-4 border border-surface-subtle bg-white p-5 md:p-6"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
              {propertyDetailContent.overview.eyebrow}
            </p>
            <a
              className="text-sm font-semibold text-brand-primary underline underline-offset-4"
              href={buildDirectionsHref(address)}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                trackPublicPropertyAction({
                  propertyId: summary.id,
                  propertyCode: summary.propertyCode,
                  action: 'directions_click',
                });
              }}
            >
              {propertyDetailContent.overview.viewOnMapLabel}
            </a>
          </div>
          <h2 className="text-2xl font-semibold text-brand-primary">{overviewTitle}</h2>
          <p className="text-sm font-medium text-text-primary/70 md:text-base">{address}</p>
          <p className="text-base leading-relaxed text-text-primary/80">{marketingSummary}</p>
        </article>

        <HighlightsSection summary={summary} content={propertyDetailContent.highlights} />

        <MapCard
          address={address}
          directionsHref={buildDirectionsHref(address)}
          content={propertyDetailContent.location}
          onView={() => {
            trackPublicPropertyAction({
              propertyId: summary.id,
              propertyCode: summary.propertyCode,
              action: 'map_view',
            });
          }}
        />
      </div>
    </>
  );
}

function AvailabilityStrip({
  totalUnits,
  leaseableCount,
  readinessStates,
  content,
}: {
  totalUnits: number;
  leaseableCount: number;
  readinessStates: UnitAvailabilityStatus[];
  content: PropertyDetailContent['availabilityStrip'];
}): JSX.Element {
  const readinessBadges = Array.from(new Set(readinessStates));
  const showNoUnits = totalUnits === 0;
  const availabilityLabel = applyTemplate(content.availableUnitsTemplate, {
    available: leaseableCount,
    total: totalUnits,
  });

  return (
    <section className="flex flex-wrap items-center gap-3 border border-surface-subtle bg-white px-4 py-3">
      <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-semibold text-brand-primary">
        <span className="h-2 w-2 rounded-full bg-brand-primary" aria-hidden="true" />
        {showNoUnits ? content.noUnitsLabel : availabilityLabel}
      </div>
      {showNoUnits ? null : (
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/70">
          {readinessBadges.map((status) => (
            <span
              key={status}
              className="rounded-full bg-surface-subtle px-3 py-1 text-text-primary/80"
            >
              {PUBLIC_READINESS_LABELS[status]}
            </span>
          ))}
        </div>
      )}
      <div className="hidden text-xs text-text-primary/60 md:block">{content.updatedLabel}</div>
    </section>
  );
}

function MobileSectionNav({
  activeSection,
  onNavigate,
  labels,
}: {
  activeSection: 'overview' | 'amenities' | 'location' | 'availability';
  onNavigate: (sectionId: string) => void;
  labels: PropertyDetailContent['navigation'];
}): JSX.Element {
  const items: Array<{ id: string; label: string; key: typeof activeSection }> = [
    { id: 'overview-section', label: labels.overviewLabel, key: 'overview' },
    { id: 'amenities-section', label: labels.highlightsLabel, key: 'amenities' },
    { id: 'location-section', label: labels.locationLabel, key: 'location' },
    { id: 'units-section', label: labels.availabilityLabel, key: 'availability' },
  ];

  return (
    <nav className="sticky top-16 z-30 border-b border-surface-subtle bg-surface-cream/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-surface-cream/80 md:hidden">
      <ul className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/70">
        {items.map((item) => (
          <li key={item.id} className="flex-1">
            <button
              type="button"
              className={clsx(
                'min-h-11 w-full rounded-full px-3 py-2 text-center transition',
                activeSection === item.key
                  ? 'bg-brand-primary text-white shadow-sm'
                  : 'bg-white text-text-primary/70 hover:bg-brand-primary/5',
              )}
              onClick={() => {
                onNavigate(item.id);
              }}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function DesktopSectionNav({
  activeSection,
  onNavigate,
  labels,
}: {
  activeSection: 'overview' | 'amenities' | 'location' | 'availability';
  onNavigate: (sectionId: string) => void;
  labels: PropertyDetailContent['navigation'];
}): JSX.Element {
  const items: Array<{ id: string; label: string; key: typeof activeSection }> = [
    { id: 'overview-section', label: labels.overviewLabel, key: 'overview' },
    { id: 'amenities-section', label: labels.highlightsLabel, key: 'amenities' },
    { id: 'location-section', label: labels.locationLabel, key: 'location' },
    { id: 'units-section', label: labels.availabilityLabel, key: 'availability' },
  ];

  return (
    <nav className="sticky top-16 z-20 hidden border border-surface-subtle bg-white/90 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 md:block">
      <ul className="flex items-center gap-4 text-xs font-semibold uppercase tracking-wide text-text-primary/70">
        {items.map((item) => {
          const isActive = activeSection === item.key;
          return (
            <li key={item.id}>
              <button
                type="button"
                className={clsx(
                  'rounded-full px-4 py-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                  isActive
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'bg-surface-subtle text-text-primary/70 hover:bg-brand-primary/5',
                )}
                onClick={() => {
                  onNavigate(item.id);
                }}
              >
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function MapEmbed({ address, onView }: { address: string; onView: () => void }): JSX.Element {
  const src = buildMapEmbedUrl(address);

  return (
    <div className="relative overflow-hidden border border-surface-subtle bg-white">
      <iframe
        title={`Map for ${address}`}
        src={src}
        onLoad={onView}
        className="h-[280px] w-full border-0 lg:h-[360px]"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

function MapCard({
  address,
  directionsHref,
  content,
  onView,
}: {
  address: string;
  directionsHref: string;
  content: PropertyDetailContent['location'];
  onView: () => void;
}): JSX.Element {
  return (
    <section
      id="location-section"
      className="flex w-full scroll-mt-28 flex-col gap-3 border border-surface-subtle bg-white p-5 md:p-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-brand-primary">{content.title}</h3>
        <a
          className="text-sm font-semibold text-brand-primary underline underline-offset-4"
          href={directionsHref}
          target="_blank"
          rel="noreferrer"
        >
          {content.directionsLabel}
        </a>
      </div>
      <MapEmbed
        address={address}
        onView={() => {
          onView();
        }}
      />
    </section>
  );
}

function HighlightsSection({
  summary,
  content,
}: {
  summary: PublicPropertySummary;
  content: PropertyDetailContent['highlights'];
}): JSX.Element {
  const floorPlanImageUrl =
    typeof summary.floorPlanImageUrl === 'string' ? summary.floorPlanImageUrl.trim() : '';
  const highlights = [
    summary.hasPotentiallyDivisible ? content.divisibleLabel : null,
    summary.hasIncentives ? content.incentivesLabel : null,
    floorPlanImageUrl.length > 0 ? content.floorPlanLabel : null,
  ].filter(Boolean) as string[];

  return (
    <section
      id="amenities-section"
      className="flex w-full scroll-mt-28 flex-col gap-3 border border-surface-subtle bg-white p-5 md:p-6"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
        {content.eyebrow}
      </p>
      <h3 className="text-xl font-semibold text-brand-primary">{content.title}</h3>
      <p className="text-sm leading-relaxed text-text-primary/70">{content.description}</p>
      {highlights.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-2 md:grid-cols-3">
          {highlights.map((highlight) => (
            <AmenityBullet key={highlight} label={highlight} />
          ))}
        </div>
      ) : (
        <p className="pt-2 text-sm text-text-primary/70">{content.emptyMessage}</p>
      )}
    </section>
  );
}

function AmenityBullet({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm text-text-primary/80">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold text-brand-primary">
        ✓
      </span>
      <span className="truncate">{label}</span>
    </div>
  );
}

interface UnitsAccordionProps {
  summary: PublicPropertySummary;
  units: PublishedUnitSummary[];
  totalUnits: number;
  activeUnitId: string | null;
  onSelect: (unit: PublishedUnitSummary) => void;
  unitPreviewImageUrl: string | null;
  bookingEmail?: string | null;
  content: PropertyDetailContent['units'];
  mapUnitRowContent: AvailabilitiesMapUnitRowContent;
  availabilityLabels: PropertyDetailContent['labels'];
}

export function UnitsAccordion({
  summary,
  units,
  totalUnits,
  activeUnitId,
  onSelect,
  unitPreviewImageUrl,
  bookingEmail,
  content,
  mapUnitRowContent,
  availabilityLabels,
}: UnitsAccordionProps): JSX.Element {
  const fallbackPreviewImageSrc =
    typeof unitPreviewImageUrl === 'string' && unitPreviewImageUrl.length > 0
      ? unitPreviewImageUrl
      : null;

  if (units.length === 0) {
    const emptyMessage = totalUnits === 0 ? content.emptyAllMessage : content.emptyFilteredMessage;
    return (
      <section className="border border-dashed border-surface-subtle bg-surface-subtle/30 p-6 text-sm text-text-primary/70">
        {emptyMessage}
      </section>
    );
  }

  const unitCountLabel = formatCountLabel(
    units.length,
    content.unitCountSingular,
    content.unitCountPlural,
  );

  return (
    <section className="flex w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-primary">{content.eyebrow}</h2>
        <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {unitCountLabel}
        </span>
      </div>
      <ul className="flex w-full flex-col gap-4">
        {units.map((unit) => {
          const hasUnitContact =
            typeof unit.contactEmail === 'string' && unit.contactEmail.length > 0;
          const contactEmail =
            typeof bookingEmail === 'string' && bookingEmail.trim().length > 0
              ? bookingEmail.trim()
              : hasUnitContact
                ? unit.contactEmail
                : null;
          const heroUrl = typeof unit.heroImageUrl === 'string' ? unit.heroImageUrl.trim() : '';
          const hasHeroImage = heroUrl.length > 0;
          const hasFallbackPreview =
            typeof fallbackPreviewImageSrc === 'string' && fallbackPreviewImageSrc.length > 0;
          let unitPreview: string | null = null;
          if (hasHeroImage) {
            unitPreview = heroUrl;
          } else if (hasFallbackPreview && typeof fallbackPreviewImageSrc === 'string') {
            unitPreview = fallbackPreviewImageSrc;
          }
          const isActive = unit.id === activeUnitId;
          const unitDisplay = formatUnitLabel(unit);
          const unitLabel = applyTemplate(mapUnitRowContent.unitLabelTemplate, {
            unitLabel: unitDisplay,
            unitCode: unit.unitCode,
            level: unit.floorLevel,
            floor: unit.floorLevel,
            floorLevel: unit.floorLevel,
          });
          const levelLabel = applyTemplate(mapUnitRowContent.levelLabelTemplate, {
            unitLabel: unitDisplay,
            unitCode: unit.unitCode,
            level: unit.floorLevel,
            floor: unit.floorLevel,
            floorLevel: unit.floorLevel,
          });
          const contactValue = contactEmail ?? content.contactFallback;

          return (
            <li key={unit.id} className="w-full">
              <article
                className={clsx(
                  'flex w-full min-w-0 gap-3 border bg-white p-3 transition hover:-translate-y-[1px]',
                  isActive ? 'border-brand-primary/60' : 'border-surface-subtle',
                )}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onSelect(unit);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(unit);
                  }
                }}
              >
                <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-xl border border-surface-subtle bg-surface-subtle/40">
                  {unitPreview !== null ? (
                    <img
                      src={unitPreview}
                      alt={`Preview for unit ${unitDisplay}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-primary/10 via-brand-accent/10 to-surface-subtle px-2 text-center text-[11px] font-semibold uppercase tracking-wide text-brand-primary/80">
                      {content.floorPlanFallback}
                    </div>
                  )}
                  <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-white/90 px-2 py-1 text-[11px] font-semibold uppercase leading-tight tracking-wide text-brand-primary">
                    {PUBLIC_READINESS_LABELS[unit.availabilityStatus]}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="text-base font-semibold text-brand-primary">{unitLabel}</p>
                      <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                        {levelLabel}
                      </p>
                    </div>
                    <ReadinessBadge status={unit.availabilityStatus} />
                  </div>
                  <p className="text-sm font-semibold text-brand-primary">
                    {formatArea(unit.areaSF, unit.minDivisibleSF)}
                  </p>
                  <p className="text-sm font-semibold text-text-primary">
                    {content.monthlyLeaseLabel}: {formatMonthlyLeaseAmount(unit)}
                  </p>
                  <p className="text-sm text-text-primary/80">{formatRent(unit)}</p>
                  <p className="text-xs text-text-primary/60">
                    {formatUnitAvailability(
                      unit.availabilityStart,
                      unit.possessionTimeline,
                      availabilityLabels,
                    )}
                    {unit.potentiallyDivisible ? ` · ${mapUnitRowContent.divisibleLabel}` : null}
                  </p>
                  {contactValue ? (
                    <p className="text-xs text-text-primary/60">
                      {content.contactLabel}:{' '}
                      <span className="font-semibold text-text-primary/80">{contactValue}</span>
                    </p>
                  ) : null}
                  <div className="flex flex-col gap-2 pt-1">
                    <UnitShowingTriggerButton
                      summary={summary}
                      unit={unit}
                      label={content.bookButtonLabel}
                      className="w-full text-center"
                      onCtaClick={() => {
                        onSelect(unit);
                      }}
                      onCtaBlocked={() => {
                        onSelect(unit);
                      }}
                    />
                    <Link
                      to={`/properties/${summary.propertyCode.toLowerCase()}/units/${unit.id}`}
                      className="text-sm font-semibold text-brand-primary underline underline-offset-4 transition hover:text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(unit);
                      }}
                    >
                      {content.viewDetailsLabel}
                    </Link>
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function UnitsTable({
  summary,
  units,
  allUnits,
  activeUnitId,
  filters,
  filterOptions,
  bookingEmail,
  unitsContent,
  unitsTableContent,
  unitFiltersContent,
  mapUnitRowContent,
  availabilityLabels,
  onFiltersChange,
  unitPreviewImageUrl,
  onSelect,
}: {
  summary: PublicPropertySummary;
  units: PublishedUnitSummary[];
  allUnits: PublishedUnitSummary[];
  activeUnitId: string | null;
  filters: UnitFiltersState;
  filterOptions: UnitFilterOptions;
  bookingEmail: string | null;
  unitsContent: PropertyDetailContent['units'];
  unitsTableContent: PropertyDetailContent['unitsTable'];
  unitFiltersContent: PropertyDetailContent['unitFilters'];
  mapUnitRowContent: AvailabilitiesMapUnitRowContent;
  availabilityLabels: PropertyDetailContent['labels'];
  onFiltersChange: (next: UnitFiltersState) => void;
  unitPreviewImageUrl: string | null;
  onSelect: (unit: PublishedUnitSummary) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const previewImageUrl = unitPreviewImageUrl ?? summary.coverImageUrl ?? null;
  const bookingEmailForUnits =
    typeof bookingEmail === 'string' && bookingEmail.trim().length > 0 ? bookingEmail.trim() : null;
  const noUnitsAvailable = allUnits.length === 0;
  const noMatches = units.length === 0;

  const handleRowClick = (unit: PublishedUnitSummary) => {
    onSelect(unit);
    navigate(`/properties/${summary.propertyCode.toLowerCase()}/units/${unit.id}`);
  };

  return (
    <div className="flex flex-col gap-6" data-ui="public.PropertyDetail.PriceRegion">
      <div className="space-y-4 md:hidden">
        <UnitFilters
          filters={filters}
          options={filterOptions}
          onChange={onFiltersChange}
          totalUnits={allUnits.length}
          content={unitFiltersContent}
        />
        <UnitsAccordion
          summary={summary}
          units={units}
          totalUnits={allUnits.length}
          activeUnitId={activeUnitId}
          onSelect={onSelect}
          unitPreviewImageUrl={previewImageUrl}
          bookingEmail={bookingEmailForUnits}
          content={unitsContent}
          mapUnitRowContent={mapUnitRowContent}
          availabilityLabels={availabilityLabels}
        />
      </div>
      <div className="hidden md:block">
        <div className="overflow-hidden border border-surface-subtle bg-white">
          <div className="border-b border-surface-subtle/80 bg-surface-subtle/40 px-5 py-4">
            <UnitFilters
              filters={filters}
              options={filterOptions}
              onChange={onFiltersChange}
              totalUnits={allUnits.length}
              dense
              content={unitFiltersContent}
            />
          </div>
          {noUnitsAvailable ? (
            <p className="rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center text-sm font-semibold text-text-primary">
              {unitsTableContent.noUnitsMessage}
            </p>
          ) : noMatches ? (
            <p className="rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center text-sm text-text-primary/70">
              {unitsTableContent.noMatchesMessage}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-text-primary/80">
                <thead className="bg-surface-subtle/60 text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3">{unitsTableContent.suiteHeader}</th>
                    <th className="whitespace-nowrap px-4 py-3">{unitsTableContent.floorHeader}</th>
                    <th className="whitespace-nowrap px-4 py-3">{unitsTableContent.sizeHeader}</th>
                    <th className="whitespace-nowrap px-4 py-3">
                      {unitsTableContent.monthlyLeaseHeader}
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">{unitsTableContent.rateHeader}</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right">
                      {unitsTableContent.actionHeader}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((unit, index) => {
                    const unitDisplay = formatUnitLabel(unit);
                    return (
                      <tr
                        key={unit.id}
                        className={clsx(
                          'cursor-pointer border-t border-surface-subtle/80 transition hover:bg-surface-subtle/40',
                          index === 0 ? 'border-t-0' : null,
                        )}
                        onClick={() => {
                          handleRowClick(unit);
                        }}
                      >
                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-brand-primary">
                          <button
                            type="button"
                            className="underline underline-offset-4"
                            onClick={() => {
                              onSelect(unit);
                            }}
                          >
                            {unitDisplay}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{unit.floorLevel}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {formatArea(unit.areaSF, unit.minDivisibleSF)}
                          {unit.potentiallyDivisible
                            ? ` · ${mapUnitRowContent.divisibleLabel}`
                            : null}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {formatMonthlyLeaseAmount(unit)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{formatRent(unit)}</td>
                        <td
                          className="whitespace-nowrap px-4 py-3"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          <div className="flex items-center justify-end">
                            <UnitShowingTriggerButton
                              summary={summary}
                              unit={unit}
                              label={unitsContent.bookButtonLabel}
                              className="min-w-[160px] text-center"
                              appearance="outline"
                              onCtaClick={() => {
                                onSelect(unit);
                              }}
                              onCtaBlocked={() => {
                                onSelect(unit);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UnitFilters({
  filters,
  options,
  onChange,
  totalUnits,
  dense = false,
  content,
}: {
  filters: UnitFiltersState;
  options: UnitFilterOptions;
  onChange: (next: UnitFiltersState) => void;
  totalUnits: number;
  dense?: boolean;
  content: PropertyDetailContent['unitFilters'];
}): JSX.Element {
  const controlClass =
    'w-full rounded-xl border border-surface-subtle bg-white px-3 py-2.5 text-base transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm';
  const labelClass = clsx(
    'text-xs font-semibold uppercase tracking-wide text-text-primary/60',
    dense ? 'text-[11px]' : null,
  );
  const wrapperClass = dense ? 'grid gap-3 md:grid-cols-3' : 'grid gap-4 md:grid-cols-3';
  const sizeAllLabel = applyTemplate(content.sizeAllLabel, { count: totalUnits });

  const handleChange = (key: keyof UnitFiltersState) => (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...filters,
      [key]: event.target.value as UnitFilterValue,
    });
  };

  return (
    <div className={wrapperClass}>
      <label className="flex flex-col gap-1">
        <span className={labelClass}>{content.sizeLabel}</span>
        <select value={filters.size} onChange={handleChange('size')} className={controlClass}>
          <option value="all">{sizeAllLabel}</option>
          {options.sizes.map((size) => (
            <option key={size} value={String(size)}>
              {formatNumber(size)} sq ft
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className={labelClass}>{content.floorLabel}</span>
        <select value={filters.floor} onChange={handleChange('floor')} className={controlClass}>
          <option value="all">{content.floorAllLabel}</option>
          {options.floors.map((floor) => (
            <option key={floor} value={floor}>
              {content.floorLabel} {floor}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className={labelClass}>{content.rateLabel}</span>
        <select value={filters.price} onChange={handleChange('price')} className={controlClass}>
          <option value="all">{content.rateAllLabel}</option>
          {options.prices.map((price) => (
            <option key={price} value={price}>
              {price}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function ReadinessBadge({ status }: { status: UnitAvailabilityStatus }): JSX.Element {
  const label = PUBLIC_READINESS_LABELS[status];
  const tone = LEASEABLE_UNIT_STATUSES.has(status)
    ? 'bg-brand-primary/10 text-brand-primary'
    : 'bg-surface-subtle text-text-primary/70';
  return (
    <span
      className={clsx(
        'rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase leading-tight tracking-wide md:px-3 md:text-xs',
        tone,
      )}
    >
      {label}
    </span>
  );
}

type KeyFigureStatKey = 'rentable' | 'units' | 'incentives' | 'divisible';
type KeyFigureStat = { key: KeyFigureStatKey; label: string; value: string };

const KEY_FIGURE_ICONS: Record<KeyFigureStatKey, string> = {
  rentable: '▢',
  units: '#',
  incentives: '★',
  divisible: '⇄',
};

function KeyFiguresCard({
  stats,
  content,
}: {
  stats: KeyFigureStat[];
  content: PropertyDetailKeyFiguresContent;
}): JSX.Element {
  return (
    <section className="w-full border border-surface-subtle bg-white p-5 md:p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-brand-primary">{content.title}</h3>
        <div className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {content.subtitle}
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-4 text-sm text-text-primary/80 sm:grid-cols-4">
        {stats.map(({ label, value, key }) => {
          const icon = KEY_FIGURE_ICONS[key];
          return (
            <div
              key={key}
              className="flex items-start gap-3 border border-surface-subtle/80 px-3 py-2"
            >
              <div className="flex h-10 w-10 items-center justify-center bg-brand-primary/10 text-base text-brand-primary">
                {icon}
              </div>
              <div className="flex flex-col">
                <dd className="text-base font-semibold text-brand-primary">{value}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/50">
                  {label}
                </dt>
              </div>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function PropertyDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-80 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-[420px] w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function resolveUnitPreviewImage(
  summary: PublicPropertySummary,
  cover?: { url: string; alt: string } | null,
): string | null {
  const floorPlanImageUrl = summary.floorPlanImageUrl;
  const hasFloorPlanImage =
    typeof floorPlanImageUrl === 'string' && floorPlanImageUrl.trim().length > 0;
  if (hasFloorPlanImage) {
    return floorPlanImageUrl.trim();
  }

  if (typeof cover?.url === 'string' && cover.url.length > 0) {
    return cover.url;
  }

  const heroFallback = summary.heroMediaUrls.find(
    (url) => typeof url === 'string' && url.trim().length > 0,
  );
  if (typeof heroFallback === 'string' && heroFallback.trim().length > 0) {
    return heroFallback.trim();
  }

  return null;
}

function resolveCover(summary: PublicPropertySummary): { url: string; alt: string } | null {
  if (typeof summary.coverImageUrl === 'string' && summary.coverImageUrl.trim().length > 0) {
    return {
      url: summary.coverImageUrl.trim(),
      alt: summary.coverImageAlt ?? formatAddress(summary),
    };
  }

  const fallback = summary.heroMediaUrls[0];
  if (typeof fallback === 'string' && fallback.trim().length > 0) {
    return { url: fallback.trim(), alt: formatAddress(summary) };
  }

  return null;
}

function buildHeroGallery(summary: PublicPropertySummary): Array<{ url: string; alt: string }> {
  const baseAlt = formatAddress(summary);
  const urls = [
    summary.coverImageUrl,
    ...(Array.isArray(summary.heroMediaUrls) ? summary.heroMediaUrls : []),
  ];
  const seen = new Set<string>();
  const gallery: Array<{ url: string; alt: string }> = [];

  urls.forEach((url) => {
    if (typeof url !== 'string') {
      return;
    }
    const trimmed = url.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    gallery.push({ url: trimmed, alt: `Photo ${String(gallery.length + 1)} of ${baseAlt}` });
  });

  return gallery;
}

function buildUnitFilterOptions(units: PublishedUnitSummary[]): UnitFilterOptions {
  const sizes = Array.from(new Set(units.map((unit) => unit.areaSF))).sort((a, b) => a - b);
  const floors = Array.from(new Set(units.map((unit) => unit.floorLevel))).sort();
  const prices = Array.from(new Set(units.map((unit) => formatRent(unit)))).sort((a, b) =>
    a.localeCompare(b),
  );

  return { sizes, floors, prices };
}

function filterUnits(
  units: PublishedUnitSummary[],
  filters: UnitFiltersState,
): PublishedUnitSummary[] {
  return units.filter((unit) => {
    const sizeMatch =
      filters.size === 'all' ||
      unit.areaSF === Number(filters.size) ||
      formatNumber(unit.areaSF) === filters.size;
    const floorMatch = filters.floor === 'all' || unit.floorLevel === filters.floor;
    const priceMatch = filters.price === 'all' || formatRent(unit) === filters.price;
    return sizeMatch && floorMatch && priceMatch;
  });
}

function buildStats(
  summary: PublicPropertySummary,
  content: PropertyDetailKeyFiguresContent,
): KeyFigureStat[] {
  return [
    {
      key: 'rentable',
      label: content.totalRentableLabel,
      value: formatNumber(summary.totalRentableSf),
    },
    { key: 'units', label: content.unitsLiveLabel, value: summary.publishedUnitCount.toString() },
    {
      key: 'incentives',
      label: content.incentivesLabel,
      value: summary.hasIncentives ? 'Available' : 'Standard terms',
    },
    {
      key: 'divisible',
      label: content.divisibleLabel,
      value: summary.hasPotentiallyDivisible ? 'Yes' : 'No',
    },
  ];
}

function findDefaultUnitId(units: PublishedUnitSummary[]): string | null {
  const leaseable = units.find((unit) => LEASEABLE_UNIT_STATUSES.has(unit.availabilityStatus));
  if (leaseable) {
    return leaseable.id;
  }
  return units[0]?.id ?? null;
}

function formatAddress(summary: PublicPropertySummary): string {
  const { streetNumber, streetName, streetType, streetDirection, city, province, postalCode } =
    summary.address;

  const line1 = [
    humanizeAddressText(streetNumber),
    humanizeAddressText(streetName),
    humanizeAddressText(streetType),
    humanizeAddressText(streetDirection),
  ]
    .filter(Boolean)
    .join(' ');

  const cityLine = [humanizeAddressText(city), humanizeAddressText(province)]
    .filter(Boolean)
    .join(', ');

  const regionLine = [cityLine, humanizeAddressText(postalCode)].filter(Boolean).join(' ');

  return [line1, regionLine].filter(Boolean).join(', ');
}

function buildPostalAddress(address: PropertyAddress): Record<string, string> {
  const line1 = [
    humanizeAddressText(address.streetNumber),
    humanizeAddressText(address.streetName),
    humanizeAddressText(address.streetType),
    humanizeAddressText(address.streetDirection),
  ]
    .filter(Boolean)
    .join(' ');
  const suite = humanizeAddressText(address.suiteNumber ?? '');
  const streetAddress = suite.length > 0 ? `${suite} ${line1}` : line1;

  return {
    '@type': 'PostalAddress',
    streetAddress,
    addressLocality: humanizeAddressText(address.city),
    addressRegion: humanizeAddressText(address.province),
    postalCode: humanizeAddressText(address.postalCode),
    addressCountry: 'CA',
  };
}

function buildPropertyStructuredData(
  summary: PublicPropertySummary,
  canonicalUrl: string,
): Record<string, unknown> {
  const images = [summary.coverImageUrl, ...summary.heroMediaUrls]
    .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .map((url) => url.trim());

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: formatAddress(summary),
    description: summary.marketingSummary,
    url: canonicalUrl,
    image: images.length > 0 ? images : undefined,
    address: buildPostalAddress(summary.address),
    category: summary.assetType,
    floorSize: {
      '@type': 'QuantitativeValue',
      value: summary.totalRentableSf,
      unitCode: 'SF',
    },
    offers: {
      '@type': 'Offer',
      availability: summary.hasVacancy
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    },
  };
}

function resolveCanonicalUrl(path: string): string {
  if (typeof window === 'undefined') {
    return path;
  }
  try {
    return new URL(path, window.location.origin).href;
  } catch {
    return path;
  }
}

function buildMapEmbedUrl(address: string): string {
  const query = encodeURIComponent(address);
  return `https://www.google.com/maps?q=${query}&output=embed`;
}

function buildDirectionsHref(address: string): string {
  const query = encodeURIComponent(address);
  return `https://www.google.com/maps/dir/?api=1&destination=${query}`;
}

function formatCountLabel(count: number, singularLabel: string, pluralLabel: string): string {
  const baseLabel = count === 1 ? singularLabel : pluralLabel;
  if (baseLabel.includes('{count}')) {
    return applyTemplate(baseLabel, { count });
  }
  return `${count.toString()} ${baseLabel}`.trim();
}

function formatRentRange(
  units: PublishedUnitSummary[],
  labels: PropertyDetailContent['labels'],
): string {
  return formatAnnualNetPsfRentRange(units, labels.rentFallback);
}

function formatAvailability(
  start: string | null,
  end: string | null,
  labels: PropertyDetailContent['labels'],
): string {
  if (start === null && end === null) {
    return labels.availabilityRangeFallback;
  }

  if (start !== null && end !== null) {
    const formatter = new Intl.DateTimeFormat('en-CA', { month: 'short', year: 'numeric' });
    return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
  }

  if (start !== null) {
    return (
      formatAvailabilityDateLabel(start, { nowLabel: 'Available now' }) ||
      labels.availabilitySingleFallback
    );
  }

  return labels.availabilitySingleFallback;
}

function formatUnitAvailability(
  availabilityStart: string | null,
  possessionTimeline: string | null,
  labels: PropertyDetailContent['labels'],
): string {
  const availabilityDate = formatAvailabilityDateLabel(availabilityStart, {
    nowLabel: 'Available now',
  });
  if (availabilityDate) {
    return availabilityDate;
  }
  if (possessionTimeline === 'immediate') {
    return labels.unitAvailabilityImmediateLabel;
  }
  if (possessionTimeline === 'negotiable') {
    return labels.unitAvailabilityNegotiableLabel;
  }
  return labels.unitAvailabilityImmediateLabel;
}

function formatArea(area: number, minDivisible: number | null): string {
  const formatter = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 });
  if (minDivisible !== null && minDivisible !== area) {
    return `${formatter.format(minDivisible)} – ${formatter.format(area)} sq ft`;
  }
  return `${formatter.format(area)} sq ft`;
}

function formatRent(unit: PublishedUnitSummary): string {
  return formatAnnualPsfRentBreakdown(unit);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(value);
}
